import React, { useMemo } from 'react';
import { Users, Lock, AlertTriangle, TrendingUp } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';

/**
 * Dashboard RH avancé — débloqué par Nadia Cheikh (Responsable RH).
 * Donne une vue agrégée de la satisfaction + identifie les chauffeurs à
 * risque de démission (satisfaction < 40 OU surcharge > 16 trips/mois).
 *
 * Conçu pour s'intégrer en haut de la page HR.jsx, juste sous le KPI bar
 * existant. Fail-safe quand 0 chauffeur dans la flotte.
 */
export default function HRDashboardPanel() {
  const hasSupportUnlock = useGameStore(s => s.hasSupportUnlock);
  const drivers = useGameStore(s => s.drivers || EMPTY_ARRAY);
  const isUnlocked = typeof hasSupportUnlock === 'function' && hasSupportUnlock('hr_dashboard');

  const stats = useMemo(() => {
    if (drivers.length === 0) {
      return { avg: 0, atRisk: [], topPerformers: [], totalTrips: 0 };
    }
    const avg = drivers.reduce((s, d) => s + (d.satisfaction || 0), 0) / drivers.length;
    const atRisk = drivers.filter(d =>
      (d.satisfaction || 0) < 40 || (d.monthlyTrips || 0) > 16
    );
    const topPerformers = [...drivers]
      .filter(d => (d.satisfaction || 0) >= 70)
      .sort((a, b) => (b.competence || 0) - (a.competence || 0))
      .slice(0, 3);
    const totalTrips = drivers.reduce((s, d) => s + (d.totalTrips || 0), 0);
    return { avg, atRisk, topPerformers, totalTrips };
  }, [drivers]);

  if (!isUnlocked) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-amber-700/40 p-4"
        data-testid="hr-dashboard-locked"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-amber-500/15">
            <Lock className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">Tableau de bord RH — Responsable RH requis</p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Recrutez Nadia Cheikh (<strong className="text-slate-200">Responsable RH</strong>) pour activer
              un dashboard agrégé qui identifie automatiquement vos chauffeurs à risque (satisfaction basse ou
              surcharge) et vos meilleurs performeurs.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink-800 rounded-lg border border-pink-700/30 p-4" data-testid="hr-dashboard-panel">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-pink-300" />
        <p className="text-sm font-semibold text-slate-200">Tableau de bord RH</p>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/30">
          Responsable RH
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-ink-700/60 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500">Satisfaction moyenne</p>
          <p className={`text-sm font-bold ${stats.avg >= 60 ? 'text-emerald-300' : stats.avg >= 40 ? 'text-amber-300' : 'text-rose-300'}`}>
            {Math.round(stats.avg)} / 100
          </p>
        </div>
        <div className="bg-ink-700/60 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500">À risque</p>
          <p className="text-sm font-bold text-rose-300">{stats.atRisk.length} chauffeur{stats.atRisk.length > 1 ? 's' : ''}</p>
        </div>
        <div className="bg-ink-700/60 rounded p-2 text-center">
          <p className="text-[10px] text-slate-500">Trips totaux</p>
          <p className="text-sm font-bold text-slate-200">{stats.totalTrips}</p>
        </div>
      </div>

      {stats.atRisk.length > 0 && (
        <div className="bg-rose-900/20 border border-rose-700/30 rounded p-2.5 mb-2" data-testid="hr-atrisk-list">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-300" />
            <p className="text-xs font-semibold text-rose-200">Action recommandée</p>
          </div>
          <ul className="text-[11px] space-y-1">
            {stats.atRisk.slice(0, 4).map(d => {
              const reason = (d.monthlyTrips || 0) > 16
                ? `surcharge (${d.monthlyTrips} trips/mois)`
                : `satisfaction ${d.satisfaction || 0}/100`;
              return (
                <li key={d.id} className="flex justify-between text-slate-300">
                  <span className="truncate max-w-[170px]" title={d.name}>{d.name}</span>
                  <span className="text-rose-300">{reason}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {stats.topPerformers.length > 0 && (
        <div className="bg-emerald-900/20 border border-emerald-700/30 rounded p-2.5" data-testid="hr-top-performers">
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-300" />
            <p className="text-xs font-semibold text-emerald-200">Top performeurs (à fidéliser)</p>
          </div>
          <ul className="text-[11px] space-y-1">
            {stats.topPerformers.map(d => (
              <li key={d.id} className="flex justify-between text-slate-300">
                <span className="truncate max-w-[170px]" title={d.name}>{d.name}</span>
                <span className="text-emerald-300">
                  sat {d.satisfaction || 0} · comp {d.competence || 0}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
